/**
 * EditRoleDialog — edit a platform user's role assignments. Extracted from
 * `routes/admin/users/index.tsx` (issue #1411).
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { PlatformUser, UserRole } from '../../types/users';
import { RoleCheckboxList } from './RoleCheckboxList';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';

export interface EditRoleDialogProps {
  open: boolean;
  user: PlatformUser | null;
  isSubmitting: boolean;
  onSubmit: (roles: UserRole[]) => void;
  onCancel: () => void;
}

export function EditRoleDialog({
  open,
  user,
  isSubmitting,
  onSubmit,
  onCancel,
}: EditRoleDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const titleId = useId();
  const roleIdPrefix = useId();
  const [roles, setRoles] = useState<UserRole[]>(['Read-Only']);

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.open = open;
  }, [open]);

  useEffect(() => {
    if (user)
      setRoles((user.roles as UserRole[]).length > 0 ? (user.roles as UserRole[]) : ['Read-Only']);
  }, [user]);

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  return (
    <DialogElement
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby={titleId}
      className="users-dialog"
      data-testid="edit-role-dialog"
    >
      <div className="users-dialog-content">
        <div className="users-dialog-header">
          <h2 id={titleId} className="forge-typography--headline5">
            Edit Roles
          </h2>
          {user && <p className="users-dialog-subtitle">{user.email}</p>}
        </div>
        <div className="users-dialog-body">
          <div className="users-form-field">
            <span className="forge-typography--label1">Roles</span>
            <RoleCheckboxList
              selected={roles}
              onChange={setRoles}
              idPrefix={`edit-role-${roleIdPrefix}`}
            />
          </div>
        </div>
        <div className="users-dialog-actions">
          <ForgeButton type="button" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </ForgeButton>
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => onSubmit(roles)}
            disabled={isSubmitting || roles.length === 0}
            data-testid="edit-role-submit"
          >
            {isSubmitting ? 'Saving…' : 'Save'}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
